import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  Typography,
  Pagination,
  Select,
  MenuItem,
  Box,
  Collapse,
  IconButton,
  TextField,
  Button,
  Popover,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";

/** DevExtreme-style filter operators */
const TEXT_FILTER_OPS = [
  { id: "contains", label: "Contains", symbol: "∋" },
  { id: "notcontains", label: "Does not contain", symbol: "∌" },
  { id: "startswith", label: "Starts with", symbol: "a…" },
  { id: "endswith", label: "Ends with", symbol: "…z" },
  { id: "equal", label: "Equals", symbol: "=" },
  { id: "notequal", label: "Does not equal", symbol: "≠" },
];

const NUMBER_FILTER_OPS = [
  { id: "equal", label: "Equals", symbol: "=" },
  { id: "notequal", label: "Does not equal", symbol: "≠" },
  { id: "less", label: "Less than", symbol: "<" },
  { id: "greater", label: "Greater than", symbol: ">" },
  { id: "lessorequal", label: "Less than or equal to", symbol: "≤" },
  { id: "greaterorequal", label: "Greater than or equal to", symbol: "≥" },
  { id: "between", label: "Between", symbol: "⟷" },
];

function defaultOpForType(filterType) {
  return filterType === "number" ? "equal" : "contains";
}

function opsForType(filterType) {
  return filterType === "number" ? NUMBER_FILTER_OPS : TEXT_FILTER_OPS;
}

function normalizeFilterEntry(raw, filterType) {
  if (raw == null || raw === "") {
    return { value: "", valueTo: "", op: defaultOpForType(filterType) };
  }
  if (typeof raw === "string" || typeof raw === "number") {
    return {
      value: String(raw),
      valueTo: "",
      op: defaultOpForType(filterType),
    };
  }
  return {
    value: raw.value != null ? String(raw.value) : "",
    valueTo: raw.valueTo != null ? String(raw.valueTo) : "",
    op: raw.op || defaultOpForType(filterType),
  };
}

function isFilterActive(raw) {
  if (raw == null || raw === "") return false;
  if (typeof raw === "string" || typeof raw === "number") {
    return String(raw).trim() !== "";
  }
  const value = String(raw.value ?? "").trim();
  const valueTo = String(raw.valueTo ?? "").trim();
  if (raw.op === "between") return value !== "" || valueTo !== "";
  return value !== "";
}

function emptyFilter(filterType, op) {
  return {
    value: "",
    valueTo: "",
    op: op || defaultOpForType(filterType),
  };
}

function roundFilterNumber(value) {
  const n = Number(String(value ?? "").replace(/[$,]/g, ""));
  if (!Number.isFinite(n)) return NaN;
  return Math.round(n * 100) / 100;
}

function getDisplayText(row, spec) {
  const raw = row[spec.field];
  if (spec.getFilterText) return String(spec.getFilterText(row) ?? "");
  if (raw == null) return "";
  return String(raw);
}

function matchesTextOp(display, op, query) {
  const text = String(display ?? "").toLowerCase();
  const q = String(query ?? "").trim().toLowerCase();
  if (!q && op !== "equal" && op !== "notequal") return true;

  switch (op) {
    case "contains":
      return text.includes(q);
    case "notcontains":
      return !text.includes(q);
    case "startswith":
      return text.startsWith(q);
    case "endswith":
      return text.endsWith(q);
    case "equal":
      return text === q;
    case "notequal":
      return text !== q;
    default:
      return text.includes(q);
  }
}

function matchesNumberOp(raw, op, query, queryTo) {
  const valueNum = roundFilterNumber(raw);
  const q = String(query ?? "").trim().replace(/[$,]/g, "");
  const qTo = String(queryTo ?? "").trim().replace(/[$,]/g, "");

  if (op === "between") {
    if (!Number.isFinite(valueNum)) return false;
    const from = q === "" ? -Infinity : roundFilterNumber(q);
    const to = qTo === "" ? Infinity : roundFilterNumber(qTo);
    if (!Number.isFinite(from) && from !== -Infinity) return false;
    if (!Number.isFinite(to) && to !== Infinity) return false;
    return valueNum >= from && valueNum <= to;
  }

  if (q === "") return true;
  const queryNum = roundFilterNumber(q);
  if (!Number.isFinite(queryNum)) return false;
  if (!Number.isFinite(valueNum)) return false;

  switch (op) {
    case "equal":
      return valueNum === queryNum;
    case "notequal":
      return valueNum !== queryNum;
    case "less":
      return valueNum < queryNum;
    case "greater":
      return valueNum > queryNum;
    case "lessorequal":
      return valueNum <= queryNum;
    case "greaterorequal":
      return valueNum >= queryNum;
    default:
      return valueNum === queryNum;
  }
}

function matchesColumnFilter(row, spec, filterRaw) {
  const filterType = spec.filterType === "number" ? "number" : "text";
  const filter = normalizeFilterEntry(filterRaw, filterType);
  if (!isFilterActive(filter)) return true;

  const display = getDisplayText(row, spec);
  const raw = row[spec.field];

  if (filterType === "number") {
    return matchesNumberOp(raw, filter.op, filter.value, filter.valueTo);
  }
  return matchesTextOp(display, filter.op, filter.value);
}

function mergeColumnFilterConfig(columns, extraConfig = {}) {
  const config = {};
  (columns || []).forEach((col) => {
    if (!col?.field) return;
    config[col.field] = {
      filterType: col.filterType,
      getFilterText: col.getFilterText,
    };
  });
  Object.entries(extraConfig || {}).forEach(([field, spec]) => {
    config[field] = { ...(config[field] || {}), ...spec };
  });
  return config;
}

/** Client-side per-column filter matching across the full row set. */
export function applyColumnFilters(rows, filters, columnConfig = {}) {
  if (!rows?.length) return rows || [];
  const entries = Object.entries(filters || {}).filter(([, value]) =>
    isFilterActive(value)
  );
  if (!entries.length) return rows;

  return rows.filter((row) =>
    entries.every(([field, query]) =>
      matchesColumnFilter(
        row,
        { field, ...(columnConfig[field] || {}) },
        query
      )
    )
  );
}

/**
 * Filter icon next to column title. Opens a nearby popup with
 * operator + value, applied only when the user clicks Apply.
 */
function HeaderFilterButton({
  field,
  label,
  filterType = "text",
  filter,
  onChange,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState(() =>
    normalizeFilterEntry(filter, filterType)
  );
  const open = Boolean(anchorEl);
  const ops = opsForType(filterType);
  const active = isFilterActive(filter);

  const openPopup = (e) => {
    e.stopPropagation();
    setDraft(normalizeFilterEntry(filter, filterType));
    setAnchorEl(e.currentTarget);
  };

  const closePopup = () => setAnchorEl(null);

  const handleCancel = () => {
    setDraft(normalizeFilterEntry(filter, filterType));
    closePopup();
  };

  const handleReset = () => {
    const cleared = emptyFilter(filterType);
    setDraft(cleared);
    onChange?.(field, cleared);
    closePopup();
  };

  const handleApply = () => {
    if (!isFilterActive(draft)) {
      onChange?.(field, emptyFilter(filterType, draft.op));
    } else {
      onChange?.(field, { ...draft });
    }
    closePopup();
  };

  return (
    <Box
      component="span"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        alignSelf: "center",
        ml: 0.25,
        flexShrink: 0,
      }}
    >
      <IconButton
        size="small"
        aria-label={`Filter ${label}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={openPopup}
        color={active ? "primary" : "default"}
        sx={{
          p: 0.25,
          ...(active
            ? {}
            : {
                color: "rgba(255,255,255,0.85)",
                opacity: 0.9,
              }),
          "&:hover": {
            color: "common.white",
            bgcolor: "rgba(255,255,255,0.08)",
          },
        }}
      >
        <FilterListIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: 280,
              mt: 0.75,
              p: 1.5,
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            },
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mb: 1.25, color: "text.primary" }}
        >
          Filter {label}
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
          <InputLabel id={`filter-op-${field}`}>Condition</InputLabel>
          <Select
            labelId={`filter-op-${field}`}
            label="Condition"
            value={draft.op}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, op: e.target.value }))
            }
          >
            {ops.map((op) => (
              <MenuItem key={op.id} value={op.id}>
                {op.symbol} {op.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          fullWidth
          autoFocus
          label={draft.op === "between" ? "From" : "Value"}
          value={draft.value}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, value: e.target.value }))
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleApply();
            }
          }}
          sx={{ mb: draft.op === "between" ? 1.25 : 1.5 }}
        />

        {draft.op === "between" ? (
          <TextField
            size="small"
            fullWidth
            label="To"
            value={draft.valueTo}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, valueTo: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleApply();
              }
            }}
            sx={{ mb: 1.5 }}
          />
        ) : null}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            size="small"
            onClick={handleReset}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Reset
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              onClick={handleCancel}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleApply}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

const ROWS_PER_PAGE_OPTIONS = [15, 30, 50, 100];

export default function PaginatedTable({
  columns = [],
  rows = [],
  totalRowCount,
  loading = false,
  onPaginationChange,
  onSortChange,
  initialOrderBy = null,
  initialOrder = "asc",
  searchQuery,
  clientSidePagination = false,
  skeletonHeight = "27px",
  renderDetail,
  getRowKey,
  emptyMessage = "No data to display",
  showFilterRow = false,
  columnFilters = {},
  onColumnFilterChange,
  columnFilterConfig = {},
  fitWidth = false,
}) {
  const resolveRowKey = (row, index) => {
    if (typeof getRowKey === "function") return getRowKey(row, index);
    return (
      row.groupKey ??
      row.user_id ??
      row.client_id ??
      row.reseller_id ??
      row.service_id ??
      row.category_id ??
      `row-${index}`
    );
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [order, setOrder] = useState(initialOrder);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  const hasDetail = typeof renderDetail === "function";
  const colSpan = columns.length + (hasDetail ? 1 : 0);

  const onPaginationChangeRef = useRef(onPaginationChange);
  const rowsPerPageRef = useRef(rowsPerPage);
  useEffect(() => {
    onPaginationChangeRef.current = onPaginationChange;
    rowsPerPageRef.current = rowsPerPage;
  });

  const prevSearchRef = useRef(searchQuery);
  useEffect(() => {
    if (searchQuery === undefined) return;
    if (prevSearchRef.current === searchQuery) return;
    prevSearchRef.current = searchQuery;
    setPage(0);
    setExpandedKeys(new Set());
    onPaginationChangeRef.current?.(0, rowsPerPageRef.current);
  }, [searchQuery]);

  const filterKey = JSON.stringify(columnFilters);
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (!showFilterRow) return;
    if (prevFilterKeyRef.current === filterKey) return;
    prevFilterKeyRef.current = filterKey;
    setPage(0);
    setExpandedKeys(new Set());
  }, [filterKey, showFilterRow]);

  const mergedFilterConfig = useMemo(
    () => mergeColumnFilterConfig(columns, columnFilterConfig),
    [columns, columnFilterConfig]
  );

  const filteredAllRows = useMemo(
    () =>
      showFilterRow
        ? applyColumnFilters(rows, columnFilters, mergedFilterConfig)
        : rows,
    [showFilterRow, rows, columnFilters, mergedFilterConfig]
  );

  const totalCount = clientSidePagination
    ? filteredAllRows.length
    : totalRowCount ?? filteredAllRows.length;

  const displayRows = clientSidePagination
    ? filteredAllRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      )
    : filteredAllRows;

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [page, totalCount, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const from = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(page * rowsPerPage + rowsPerPage, totalCount);
  const tableMinWidth = fitWidth
    ? undefined
    : columns.reduce((sum, col) => {
        const value = col.minWidth ?? col.width;
        return sum + (typeof value === "number" ? value : 0);
      }, 0) + (hasDetail ? 48 : 0);

  const cellWidthSx = (col) =>
    fitWidth
      ? {
          width: col.width,
          minWidth: 0,
          overflow: "hidden",
        }
      : {
          width: col.width,
          minWidth: col.minWidth,
        };

  const handlePageChange = (_, value) => {
    const newPage = value - 1;
    setPage(newPage);
    setExpandedKeys(new Set());
    onPaginationChange?.(newPage, rowsPerPage);
  };

  const handleRowsPerPageChange = (e) => {
    const newSize = Number(e.target.value);
    setRowsPerPage(newSize);
    setPage(0);
    setExpandedKeys(new Set());
    onPaginationChange?.(0, newSize);
  };

  const handleSort = (field) => {
    const newOrder = orderBy === field && order === "asc" ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(field);
    setPage(0);
    setExpandedKeys(new Set());
    if (onSortChange) onSortChange(field, newOrder, rowsPerPage);
    else onPaginationChange?.(0, rowsPerPage);
  };

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const cellValue = (row, col) =>
    col.render
      ? col.render(col.field ? row[col.field] : undefined, row)
      : row[col.field] != null
        ? String(row[col.field])
        : "-";

  const detailCellSx = {
    width: fitWidth ? 44 : 48,
    minWidth: fitWidth ? 44 : 48,
    maxWidth: fitWidth ? 44 : 48,
  };

  const renderDetailCell = (content, extraSx) =>
    hasDetail ? (
      <TableCell sx={{ ...detailCellSx, ...extraSx }}>{content}</TableCell>
    ) : null;

  return (
    <>
      <TableContainer
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflowX: fitWidth ? "hidden" : "auto",
          overflowY: "hidden",
        }}
      >
        <Table
          aria-label="paginated table"
          sx={{
            width: "100%",
            minWidth: tableMinWidth || undefined,
            tableLayout: fitWidth ? "fixed" : undefined,
          }}
        >
          <TableHead
            sx={{
              bgcolor: "secondary.main",
              borderBottom: "1px solid",
              borderColor: "primary.main",
              "& .MuiTableCell-head": {
                color: "common.white",
                bgcolor: "secondary.main",
                fontWeight: 600,
                borderBottom: "none",
              },
              "& .MuiTableSortLabel-root": {
                color: "common.white",
                whiteSpace: fitWidth ? "normal" : "nowrap",
                flexWrap: "nowrap",
                "&:hover": { color: "common.white" },
                "&.Mui-active": { color: "common.white" },
              },
              "& .MuiTableSortLabel-icon": {
                color: "rgba(255, 255, 255, 0.7) !important",
              },
              "& .Mui-active .MuiTableSortLabel-icon": {
                color: "common.white !important",
              },
              "& .MuiIconButton-root": {
                color: "rgba(255, 255, 255, 0.85)",
                "&:hover": {
                  color: "common.white",
                  bgcolor: "rgba(255, 255, 255, 0.08)",
                },
              },
              "& .MuiIconButton-colorPrimary, & .MuiIconButton-root.Mui-selected":
                {
                  color: "primary.main",
                },
            }}
          >
            <TableRow>
              {columns.map((col) => {
                const canFilter =
                  showFilterRow &&
                  col.filterable !== false &&
                  Boolean(col.field);
                const label =
                  typeof col.label === "string" ? col.label : col.field;
                const filterType =
                  mergedFilterConfig[col.field]?.filterType ||
                  col.filterType ||
                  "text";
                const filtered =
                  col.filtered || isFilterActive(columnFilters[col.field]);

                return (
                  <TableCell
                    key={col.id}
                    align={col.align || "left"}
                    sx={{
                      ...cellWidthSx(col),
                      padding: fitWidth ? "8px 4px 8px 8px" : "8px 8px 8px 16px",
                      verticalAlign: "middle",
                      ...(filtered
                        ? {
                            boxShadow: (theme) =>
                              `inset 0 -2px 0 ${theme.palette.primary.main}`,
                          }
                        : null),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          col.align === "right" ? "flex-end" : "flex-start",
                        flexWrap: "nowrap",
                        gap: 0,
                        minWidth: 0,
                        width: "100%",
                        minHeight: fitWidth ? 36 : undefined,
                        whiteSpace: fitWidth ? "normal" : "nowrap",
                      }}
                    >
                      {col.sortable !== false && col.field ? (
                        <TableSortLabel
                          active={orderBy === col.field}
                          direction={orderBy === col.field ? order : "asc"}
                          onClick={() => handleSort(col.field)}
                          title={
                            typeof col.label === "string" ? col.label : undefined
                          }
                          sx={{
                            flex: "1 1 auto",
                            minWidth: 0,
                            alignItems: "center",
                            whiteSpace: fitWidth ? "normal" : "nowrap",
                            "& .MuiTableSortLabel-icon": {
                              flexShrink: 0,
                            },
                          }}
                        >
                          <Typography
                            component="span"
                            variant="body2"
                            noWrap={!fitWidth}
                            sx={{
                              fontWeight: 600,
                              minWidth: 0,
                              flex: "1 1 auto",
                              color: "inherit",
                              lineHeight: 1.2,
                              ...(fitWidth
                                ? {
                                    width: 0,
                                    whiteSpace: "normal",
                                    overflowWrap: "anywhere",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    fontSize: "0.75rem",
                                  }
                                : null),
                            }}
                          >
                            {col.label}
                          </Typography>
                        </TableSortLabel>
                      ) : typeof col.label === "string" ||
                        typeof col.label === "number" ? (
                        <Typography
                          component="span"
                          variant="body2"
                          noWrap={!fitWidth}
                          title={String(col.label)}
                          sx={{
                            fontWeight: 600,
                            minWidth: 0,
                            color: filtered ? "primary.main" : "inherit",
                            lineHeight: 1.2,
                            ...(fitWidth
                              ? {
                                  width: 0,
                                  flex: "1 1 auto",
                                  whiteSpace: "normal",
                                  overflowWrap: "anywhere",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  fontSize: "0.75rem",
                                }
                              : null),
                          }}
                        >
                          {col.label}
                        </Typography>
                      ) : (
                        col.label
                      )}
                      {canFilter ? (
                        <HeaderFilterButton
                          field={col.field}
                          label={label}
                          filterType={filterType}
                          filter={columnFilters[col.field]}
                          onChange={onColumnFilterChange}
                        />
                      ) : null}
                      {col.headerExtras != null ? col.headerExtras : null}
                    </Box>
                  </TableCell>
                );
              })}
              {renderDetailCell(null, {
                padding: "8px 8px 8px 4px",
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: rowsPerPage }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align || "left"}
                      sx={{
                        ...cellWidthSx(col),
                        padding: "6px 4px 6px 16px",
                      }}
                    >
                      <Skeleton
                        variant="text"
                        sx={{
                          maxWidth: col.width ?? "80%",
                          height: skeletonHeight ?? "27px",
                        }}
                      />
                    </TableCell>
                  ))}
                  {renderDetailCell(
                    <Skeleton variant="circular" width={24} height={24} />,
                    { padding: "6px 8px 6px 4px" }
                  )}
                </TableRow>
              ))
            ) : filteredAllRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((row, i) => {
                const rowKey = String(resolveRowKey(row, i));
                const isExpanded = expandedKeys.has(rowKey);
                return (
                  <React.Fragment key={rowKey}>
                    <TableRow
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? i % 2 === 0
                              ? "#1b1b1e"
                              : "#141416"
                            : i % 2 === 0
                              ? "#fafafa"
                              : "white",
                        "&:hover": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "#2a2a2e" : "#f5f5f5",
                        },
                      }}
                    >
                      {columns.map((col) => (
                        <TableCell
                          key={col.id}
                          align={col.align || "left"}
                          sx={{
                            ...cellWidthSx(col),
                            padding: "6px 4px 6px 16px",
                            ...(fitWidth
                              ? {
                                  whiteSpace: "normal",
                                  overflowWrap: "break-word",
                                  verticalAlign: "middle",
                                }
                              : null),
                          }}
                        >
                          {cellValue(row, col)}
                        </TableCell>
                      ))}
                      {renderDetailCell(
                        <IconButton
                          size="small"
                          aria-label={
                            isExpanded ? "Collapse row" : "Expand row"
                          }
                          onClick={() => toggleExpanded(rowKey)}
                          sx={{ color: "text.secondary" }}
                        >
                          {isExpanded ? (
                            <CollapseIcon fontSize="small" />
                          ) : (
                            <ExpandIcon fontSize="small" />
                          )}
                        </IconButton>,
                        {
                          padding: "4px 8px 4px 4px",
                          verticalAlign: "middle",
                        }
                      )}
                    </TableRow>
                    {hasDetail && (
                      <TableRow>
                        <TableCell
                          colSpan={colSpan}
                          sx={{
                            p: 0,
                            borderBottom: isExpanded ? undefined : 0,
                          }}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                bgcolor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "#141416"
                                    : "#f7f7f7",
                                borderTop: "1px solid",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                px: 2,
                                py: 1.5,
                              }}
                            >
                              {renderDetail(row)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && totalCount > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            px: 2,
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Items per page:
            </Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              sx={{ minWidth: 64, fontSize: "14px", height: "28px" }}
            >
              {ROWS_PER_PAGE_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="body2" color="text.secondary">
              {from}-{to} of {totalCount} items
            </Typography>
          </Box>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handlePageChange}
            showFirstButton
            showLastButton
            color="primary"
          />
        </Box>
      )}
    </>
  );
}
